import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { ApiError, PaginationModel, handleError } from '../../../api/base';
import {
  CreatePlanogramRequest,
  UpdatePlanogramRequest,
} from '../../../api/planogram/reqres';
import PlanogramService from '../../../api/planogram/services';
import { PlanogramModel } from '../../../models/planogram';
import { ListModelParams } from '../../../types';
import { convertParamsToQuery } from '../../../util';

export interface PlanogramState {
  value: PlanogramModel[];
  allPlanograms: PlanogramModel[];
  pagination: PaginationModel | null;
  fetchingPlanograms: boolean;
  error: { code: number; details?: string; message: string } | null;
  searchingPlanograms: boolean;
  savingPlanogram: boolean;
  totalCount: number;
  // centralized URL-state controls
  page: number;
  pageSize: number;
  search: string;
  sort: string; // e.g., 'updated_at:desc'
}

const initialState: PlanogramState = {
  value: [],
  allPlanograms: [],
  pagination: null,
  error: null,
  fetchingPlanograms: false,
  searchingPlanograms: false,
  savingPlanogram: false,
  totalCount: 0,
  // defaults for centralized URL-state
  page: 1,
  pageSize: 20,
  search: '',
  sort: 'updated_at:desc',
};

const planogramParams: ListModelParams = {
  _limit: 20,
  _order_by: 'updated_at:desc',
  _offset: 0,
  _columns: 'createdByUser.*,client.*,media_files.*',
};

export const fetchPlanograms = createAsyncThunk(
  'planogram/fetchPlanograms',
  async () => {
    const queryString = convertParamsToQuery(planogramParams);
    const resp = await PlanogramService().listPlanograms(queryString);
    return resp;
  }
);

export const paginatePlanograms = createAsyncThunk(
  'planogram/paginatePlanograms',
  async (params: ListModelParams) => {
    const queryString = convertParamsToQuery({
      ...params,
      _columns: 'createdByUser.*,client.*,media_files.*',
    });
    const resp = await PlanogramService().listPlanograms(queryString);
    return resp;
  }
);

export const createPlanogram = createAsyncThunk(
  'planogram/createPlanogram',
  async (planogram: CreatePlanogramRequest, { dispatch, rejectWithValue }) => {
    try {
      const resp = await PlanogramService().createPlanogram(planogram);
      await dispatch(fetchPlanograms());
      return resp;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updatePlanogram = createAsyncThunk(
  'planogram/updatePlanogram',
  async (
    {
      planogramId,
      planogram,
    }: { planogramId: string; planogram: UpdatePlanogramRequest },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const resp = await PlanogramService().updatePlanogram(
        planogramId,
        planogram
      );
      await dispatch(fetchPlanograms());
      return resp;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deletePlanogram = createAsyncThunk(
  'planogram/deletePlanogram',
  async (planogramId: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const resp = await PlanogramService().deletePlanogram(planogramId);
      // refresh current page respecting centralized controls
      const {
        planogram: { page, pageSize, search, sort },
      } = getState() as any;
      const params: ListModelParams = {
        _limit: pageSize,
        _offset: (page - 1) * pageSize,
        _order_by: sort || 'updated_at:desc',
        ...(search ? { name: `*${String(search).toLowerCase()}*` } : {}),
      };
      await dispatch(paginatePlanograms(params));
      return resp;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const searchPlanogramsByName = createAsyncThunk(
  'planogram/searchPlanogramsByName',
  async (query: string, { rejectWithValue }) => {
    try {
      let params: ListModelParams = {
        ...planogramParams,
      };

      if (query && query.length) {
        params = {
          ...params,
          name: `*${query.toLowerCase()}*`,
        };
      }

      const queryString = convertParamsToQuery(params);
      return await PlanogramService().listPlanograms(queryString);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const planogramSlice = createSlice({
  name: 'planogram',
  initialState,
  reducers: {
    hydrateFromUrl: (
      state,
      {
        payload,
      }: {
        payload: {
          page?: number;
          pageSize?: number;
          search?: string;
          sort?: string;
        };
      }
    ) => {
      if (payload.page !== undefined) state.page = payload.page;
      if (payload.pageSize !== undefined) state.pageSize = payload.pageSize;
      if (payload.search !== undefined) state.search = payload.search;
      if (payload.sort !== undefined) state.sort = payload.sort;
    },
    setSearchQuery: (state, { payload }: { payload: string }) => {
      state.search = payload ?? '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlanograms.pending, (state) => {
        state.fetchingPlanograms = true;
      })
      .addCase(fetchPlanograms.fulfilled, (state, action) => {
        state.fetchingPlanograms = false;
        state.value = action.payload.data;
        state.allPlanograms = action.payload.data;
        state.pagination = action.payload.pagination;
        state.totalCount = action.payload.pagination.count;
      })
      .addCase(fetchPlanograms.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.fetchingPlanograms = false;
      })
      .addCase(paginatePlanograms.pending, (state) => {
        state.fetchingPlanograms = true;
      })
      .addCase(paginatePlanograms.fulfilled, (state, action) => {
        state.fetchingPlanograms = false;
        state.value = action.payload.data;
        state.pagination = action.payload.pagination;
        state.totalCount = action.payload.pagination.count;
      })
      .addCase(paginatePlanograms.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.fetchingPlanograms = false;
      });

    builder
      .addCase(searchPlanogramsByName.pending, (state) => {
        state.searchingPlanograms = true;
      })
      .addCase(searchPlanogramsByName.fulfilled, (state, action) => {
        state.searchingPlanograms = false;
        if (action.payload) {
          state.value = action.payload?.data;
          state.pagination = action.payload?.pagination;
          state.totalCount = action.payload?.pagination.count || 0;
        }
      })
      .addCase(searchPlanogramsByName.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.searchingPlanograms = false;
      });

    builder
      .addCase(createPlanogram.pending, (state) => {
        state.savingPlanogram = true;
      })
      .addCase(createPlanogram.fulfilled, (state) => {
        state.savingPlanogram = false;
      })
      .addCase(createPlanogram.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.savingPlanogram = false;
      });

    builder
      .addCase(updatePlanogram.pending, (state) => {
        state.savingPlanogram = true;
      })
      .addCase(updatePlanogram.fulfilled, (state) => {
        state.savingPlanogram = false;
      })
      .addCase(updatePlanogram.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.savingPlanogram = false;
      });
  },
});

export const { hydrateFromUrl, setSearchQuery } = planogramSlice.actions;
